import { useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import type { EditorState } from '@tiptap/pm/state';
import { Flex, Button, IconButton, Icon, Dropdown, Menu, ColorPicker, Tooltip, icon as iconSize } from '@nula/core-ui';
import type { EditEntityKind } from '@/lib/ai-composer/types';
import type { EditActionStatus } from '../stores/use-edit-action-store';
import { computeActiveFormats } from '../hooks/use-active-formats';
import { buildAiActionItems, type AiActionPick, type AiActionTemplate } from './ai-actions-menu';
import { rangeToHtml } from './block-actions';
import { expandRangeToWholeBlocks } from './section-scope';
import { EditorToolbar, EditorToolbarDivider } from './editor-toolbar';
import styles from './editor-bubble-menu.module.css';

export interface EditorBubbleMenuProps {
  editor: Editor;
  onEditLink: () => void;
  entityId?: string;
  entityKind?: EditEntityKind;
  onAiPick?: (pick: AiActionPick & { from: number; to: number; blockContent: string }) => void;
  aiStatus?: EditActionStatus;
  selectionTemplates?: readonly AiActionTemplate[];
  onGenerateFromSelection?: (templateId: string | null) => void;
}

export function bubbleMenuShouldShow({ editor, state }: { editor: Editor; state: EditorState }): boolean {
  const { from, to } = state.selection;
  if (from === to) return false;
  if (editor.isActive('codeBlock')) return false;
  return true;
}

const BLOCK_TYPES = [
  { label: 'Paragraph', value: 'paragraph' },
  { label: 'Heading 1', value: 'h1' },
  { label: 'Heading 2', value: 'h2' },
  { label: 'Heading 3', value: 'h3' },
  { label: 'Quote', value: 'quote' },
  { label: 'Bullet list', value: 'bulletList' },
  { label: 'Numbered list', value: 'orderedList' },
];

function applyBlockType(editor: Editor, value: string) {
  const chain = editor.chain().focus();
  switch (value) {
    case 'paragraph':
      chain.setParagraph().run();
      break;
    case 'h1':
      chain.toggleHeading({ level: 1 }).run();
      break;
    case 'h2':
      chain.toggleHeading({ level: 2 }).run();
      break;
    case 'h3':
      chain.toggleHeading({ level: 3 }).run();
      break;
    case 'quote':
      chain.toggleBlockquote().run();
      break;
    case 'bulletList':
      chain.toggleBulletList().run();
      break;
    case 'orderedList':
      chain.toggleOrderedList().run();
      break;
    default:
      break;
  }
}

interface AiMenuEntry {
  key: string;
  label: string;
  type: 'item' | 'divider';
  onClick?: () => void;
  children?: { key: string; label: string; type: 'item'; onClick: () => void }[];
}

export function EditorBubbleMenu({ editor, onEditLink, entityId, entityKind, onAiPick, aiStatus, selectionTemplates, onGenerateFromSelection }: EditorBubbleMenuProps) {
  const formats = computeActiveFormats(editor);
  const highlightPickerRef = useRef<HTMLDivElement>(null);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);

  const handleAiPick = (pick: AiActionPick) => {
    setAiMenuOpen(false);
    if (!onAiPick) return;
    const sel = editor.state.selection;
    const range = pick.action === 'generate-chart'
      ? expandRangeToWholeBlocks(editor, sel.from, sel.to)
      : { from: sel.from, to: sel.to };
    const blockContent = rangeToHtml(editor, range.from, range.to);
    onAiPick({ ...pick, from: range.from, to: range.to, blockContent });
  };

  const { from: selFrom, to: selTo } = editor.state.selection;
  const allowChart = !!(entityId && entityKind) && selFrom !== selTo;
  const offerGenerate =
    !!onGenerateFromSelection &&
    !!selectionTemplates?.length &&
    editor.state.doc.textBetween(selFrom, selTo, ' ', ' ').trim() !== '';
  const aiItems = buildAiActionItems({
    allowChart,
    onPick: handleAiPick,
    selectionTemplates: offerGenerate ? selectionTemplates : [],
    onGenerateFromSelection,
  });

  const aiMenuItems: AiMenuEntry[] = aiItems.flatMap((item): AiMenuEntry[] => {
    if (item.action === 'translate' && item.languages) {
      return [{
        key: `ai-translate`,
        label: item.label,
        type: 'item',
        children: item.languages.map((lang) => ({
          key: `ai-translate-${lang.value}`,
          label: lang.label,
          type: 'item' as const,
          onClick: () => handleAiPick({ action: 'translate', params: { targetLanguage: lang.value } }),
        })),
      }];
    }
    if (item.action === 'generate-from-selection' && item.templates) {
      return [
        ...(item.dividerBefore ? [{ key: 'ai-generate-divider', label: '', type: 'divider' as const }] : []),
        {
          key: 'ai-generate',
          label: item.label,
          type: 'item',
          children: [
            ...item.templates.map((template) => ({
              key: `ai-generate:${template.id}`,
              label: template.name,
              type: 'item' as const,
              onClick: () => onGenerateFromSelection?.(template.id),
            })),
            {
              key: 'ai-generate-more',
              label: 'More templates…',
              type: 'item' as const,
              onClick: () => onGenerateFromSelection?.(null),
            },
          ],
        },
      ];
    }
    return [{
      key: `ai-${item.action}`,
      label: item.label,
      type: 'item',
      onClick: item.run,
    }];
  });

  const blockTypeLabel =
    BLOCK_TYPES.find((b) => b.value === formats.blockType)?.label ?? 'Paragraph';

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={bubbleMenuShouldShow}
    >
      <EditorToolbar>
        {/* ✨ AI actions button (only when entityId/entityKind provided) */}
        {entityId && entityKind && (
          <>
            <Tooltip title="AI actions" placement="top" mouseEnterDelay={600}>
              <IconButton
                size="sm"
                transparent
                pill
                aria-label="AI actions"
                disabled={aiStatus != null && aiStatus !== 'idle'}
                onClick={() => setAiMenuOpen((prev) => !prev)}
              >
                <Icon name="auto_awesome" size={iconSize.sm} />
              </IconButton>
            </Tooltip>
            {aiMenuOpen && (
              <Flex className={styles.aiMenuWrap}>
                <Menu
                  items={[{
                    key: 'ai-group',
                    label: '✨ AI actions',
                    type: 'group',
                    children: aiMenuItems.map((item) => ({
                      key: item.key,
                      label: item.label,
                      type: item.type,
                      onClick: item.onClick,
                      children: item.children,
                    })),
                  }]}
                  onSelect={(key) => {
                    const child = aiMenuItems.flatMap((i) => i.children ?? []).find((c) => c.key === key);
                    if (child) {
                      child.onClick();
                    } else {
                      const found = aiMenuItems.find((i) => i.key === key);
                      if (found?.onClick) found.onClick();
                    }
                    setAiMenuOpen(false);
                  }}
                />
              </Flex>
            )}
            <EditorToolbarDivider />
          </>
        )}
        {/* Block type dropdown */}
        <Dropdown
          trigger={['click']}
          menu={{
            items: BLOCK_TYPES.map((b) => ({
              key: b.value,
              label: b.label,
              onClick: () => applyBlockType(editor, b.value),
            })),
          }}
        >
          <Button
            size="sm"
            variant="text"
            className={styles.blockTypeButton}
            htmlType="button"
          >
            {blockTypeLabel}
          </Button>
        </Dropdown>

        <EditorToolbarDivider />

        {/* Basic marks — Bold Italic Underline Strike Code */}
        <Tooltip title="Bold" placement="top" mouseEnterDelay={600}>
          <IconButton size="sm" transparent pill aria-label="Bold" data-active={formats.bold}
            onClick={() => editor.chain().focus().toggleBold().run()}>
            <Icon name="format_bold" size={iconSize.sm} weight={300} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic" placement="top" mouseEnterDelay={600}>
          <IconButton size="sm" transparent pill aria-label="Italic" data-active={formats.italic}
            onClick={() => editor.chain().focus().toggleItalic().run()}>
            <Icon name="format_italic" size={iconSize.sm} weight={300} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Underline" placement="top" mouseEnterDelay={600}>
          <IconButton size="sm" transparent pill aria-label="Underline" data-active={formats.underline}
            onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <Icon name="format_underlined" size={iconSize.sm} weight={300} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Strikethrough" placement="top" mouseEnterDelay={600}>
          <IconButton size="sm" transparent pill aria-label="Strikethrough" data-active={formats.strike}
            onClick={() => editor.chain().focus().toggleStrike().run()}>
            <Icon name="strikethrough_s" size={iconSize.sm} weight={300} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Inline code" placement="top" mouseEnterDelay={600}>
          <IconButton size="sm" transparent pill aria-label="Inline code" data-active={formats.code}
            onClick={() => editor.chain().focus().toggleCode().run()}>
            <Icon name="code" size={iconSize.sm} weight={300} />
          </IconButton>
        </Tooltip>

        <EditorToolbarDivider />

        {/* Superscript / Subscript */}
        <Tooltip title="Superscript" placement="top" mouseEnterDelay={600}>
          <IconButton size="sm" transparent pill aria-label="Superscript" data-active={formats.superscript}
            onClick={() => editor.chain().focus().toggleSuperscript().run()}>
            <Icon name="superscript" size={iconSize.sm} weight={300} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Subscript" placement="top" mouseEnterDelay={600}>
          <IconButton size="sm" transparent pill aria-label="Subscript" data-active={formats.subscript}
            onClick={() => editor.chain().focus().toggleSubscript().run()}>
            <Icon name="subscript" size={iconSize.sm} weight={300} />
          </IconButton>
        </Tooltip>

        <EditorToolbarDivider />

        {/* Link */}
        <Tooltip title="Link" placement="top" mouseEnterDelay={600}>
          <IconButton size="sm" transparent pill aria-label="Link" data-active={formats.link}
            onClick={onEditLink}>
            <Icon name="link" size={iconSize.sm} weight={300} />
          </IconButton>
        </Tooltip>

        {/* Comment — dispatches an event EditorComments listens for, keeping the
            bubble menu decoupled from comment state. */}
        <Tooltip title="Comment" placement="top" mouseEnterDelay={600}>
          <IconButton size="sm" transparent pill aria-label="Comment"
            onClick={() => window.dispatchEvent(new Event('nula-editor-add-comment'))}>
            <Icon name="chat_bubble" size={iconSize.sm} weight={300} />
          </IconButton>
        </Tooltip>

        <EditorToolbarDivider />

        {/* Highlight — icon button triggers the hidden ColorPicker swatch */}
        <span className={styles.highlightPickerWrap}>
          <Tooltip title="Highlight" placement="top" mouseEnterDelay={600}>
            <IconButton
              size="sm"
              transparent
              pill
              aria-label="Highlight"
              data-active={formats.highlight}
              data-testid="highlight-color-picker"
              onClick={() => highlightPickerRef.current?.querySelector<HTMLElement>('[class*="swatch"]')?.click()}
            >
              <Icon name="ink_highlighter" size={iconSize.sm} weight={300} />
            </IconButton>
          </Tooltip>
          <div ref={highlightPickerRef} className={styles.highlightPickerHidden}>
            <ColorPicker
              size="sm"
              showText={false}
              value={formats.highlightColor ?? '#FEF9C3'}
              presets={[{ label: 'Recommended', colors: ['#FEE2E2', '#FFEDD5', '#FEF9C3', '#DCFCE7', '#CCFBF1', '#DBEAFE', '#EDE9FE', '#FCE7F3', '#E5E7EB', '#F3F3F3'] }]}
              onChange={(hex: string) => editor.chain().focus().toggleHighlight({ color: hex }).run()}
            />
          </div>
        </span>

        {/* Text color */}
        <span className={styles.colorPickerWrap} data-testid="text-color-picker">
          <ColorPicker
            size="sm"
            showText={false}
            value={formats.color ?? undefined}
            allowClear
            onChange={(hex: string) => editor.chain().focus().setColor(hex).run()}
          />
        </span>
      </EditorToolbar>
    </BubbleMenu>
  );
}
